import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import useTheme from '../theme/useTheme';
import Avatar from '../ui/Avatar';
import Btn from '../ui/Btn';
import './shell.css';

/* Session storage can throw in locked-down browsers — never let the bar die for it. */
function ss(key) {
  try {
    return sessionStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
}

export default function TopBar({ title, eyebrow, onMenu, menuOpen }) {
  const { isDark, toggle } = useTheme();
  const { userName, setIsAuth } = useContext(UserContext);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  /* The admin's session keys are its own: 'apiToken', 'userToken', 'userName',
     'isAuth', 'isAdmin', 'userUUID'. 'usrPhoto' belongs to the manager app and is
     never written here — reading it only risked showing a manager account's photo
     when both apps share an origin. The avatar falls back to initials, which is
     what this bar has always actually rendered. */
  const name = userName || ss('userName') || 'Admin';
  const role = ss('isAdmin') == 1 ? 'Administrator' : 'Back office'; // eslint-disable-line eqeqeq

  useEffect(() => {
    if (!open) return undefined;

    const onDown = (ev) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target)) setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /* Exactly what DashboardNav.js does on logout — no key added, none removed. */
  const logout = () => {
    setOpen(false);
    setIsAuth(false);
    sessionStorage.setItem('isAuth', 0);
    navigate('/login');
  };

  return (
    <header className="topbar">
      <button
        type="button"
        className="topbar-menu"
        aria-expanded={menuOpen === true}
        aria-controls="admin-nav"
        aria-label={menuOpen === true ? 'Κλείσιμο μενού' : 'Μενού'}
        onClick={onMenu}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>

      <div className="crumb">
        <h1>{title || 'Dashboard'}</h1>
        {eyebrow ? <span className="mono">{eyebrow}</span> : null}
      </div>

      <div className="topbar-actions">
        <Btn className="theme-btn" onClick={toggle} aria-label="Switch theme">
          {isDark ? (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
            </svg>
          )}
          <span className="theme-btn-label">{isDark ? 'Light' : 'Dark'}</span>
        </Btn>

        <div className="who" ref={wrapRef}>
          <button
            type="button"
            className="who-btn"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <Avatar name={name} size={32} />

            <span className="who-text">
              <span className="who-name" title={name}>{name}</span>
              <span className="who-role" title={role}>{role}</span>
            </span>

            <svg className="who-caret" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {open && (
            <div className="menu" role="menu" aria-label="Account">
              <div className="menu-head">
                <span className="menu-name">{name}</span>
                <span className="menu-role mono">{role}</span>
              </div>

              <div className="menu-sep" />

              <button
                type="button"
                className="menu-item menu-item-crit"
                role="menuitem"
                onClick={logout}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M15 17l5-5-5-5M20 12H9M12 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h6" />
                </svg>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
